import React, { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Button, Table } from 'semantic-ui-react'
import { LOAD_MANAGE_ORGANIZATIONS_QUERY } from '../../../../graphql/admin'
import {
    ORG_TYPES,
    UPDATE_ORGANIZATION_MUTATION,
} from '../../../../graphql/organizations'
import LoadingSpinner from '../../../LoadingSpinner'
import Pagination from '../../../Pagination'
import EditOrganizationModal from './EditOrganizationModal'

const PER_PAGE = 10

const formatLocation = (location) => {
    if (!location) return 'N/A'
    const parts = [location.city, location.country].filter(Boolean)
    return parts.length ? parts.join(', ') : 'N/A'
}

const OrgReview = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const [editingOrgId, setEditingOrgId] = useState(null)

    const { data, loading } = useQuery(LOAD_MANAGE_ORGANIZATIONS_QUERY)
    const { data: orgTypesData, loading: orgTypesLoading } = useQuery(ORG_TYPES)
    const [updateOrganization, { loading: updating }] = useMutation(
        UPDATE_ORGANIZATION_MUTATION
    )

    const organizationTypeOptions = useMemo(() => {
        const types = orgTypesData?.orgTypes || []

        return [...new Set(types.filter(Boolean))].map((type) => ({
            key: type,
            text: type,
            value: type,
        }))
    }, [orgTypesData])

    if (loading || orgTypesLoading) return <LoadingSpinner />

    const organizations = data?.adminPortal?.actionableOrganizations || []

    if (organizations.length === 0) return <div>No organizations</div>

    const end = currentPage * PER_PAGE
    const start = end - PER_PAGE

    const editingOrganization =
        organizations.find((org) => org.id === editingOrgId) || null

    const onSave = async (organization, organizationInput) => {
        try {
            await updateOrganization({
                variables: {
                    organizationId: organization.id,
                    organization: organizationInput,
                },
                refetchQueries: [
                    {
                        query: LOAD_MANAGE_ORGANIZATIONS_QUERY,
                    },
                ],
            })

            setEditingOrgId(null)
            alert('Organization updated')
        } catch (error) {
            alert(error.message || error)
        }
    }

    return (
        <Fragment>
            <p>
                This table shows the organizations, which are the current employers of active, non-blocked experts, that need a review.
            </p>
            <Table celled size="small">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Name</Table.HeaderCell>
                        <Table.HeaderCell>Website</Table.HeaderCell>
                        <Table.HeaderCell>Type</Table.HeaderCell>
                        <Table.HeaderCell>Status</Table.HeaderCell>
                        <Table.HeaderCell>Size</Table.HeaderCell>
                        <Table.HeaderCell>Location</Table.HeaderCell>
                        <Table.HeaderCell collapsing>Actions</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {organizations.slice(start, end).map((organization) => (
                        <Table.Row key={organization.id}>
                            <Table.Cell>{organization.name}</Table.Cell>
                            <Table.Cell>
                                {organization.url ? (
                                    <a
                                        href={organization.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {organization.url}
                                    </a>
                                ) : (
                                    'N/A'
                                )}
                            </Table.Cell>
                            <Table.Cell>
                                {organization.type || 'N/A'}
                            </Table.Cell>
                            <Table.Cell>
                                {organization.status || 'N/A'}
                            </Table.Cell>
                            <Table.Cell>
                                {organization.size || 'N/A'}
                            </Table.Cell>
                            <Table.Cell>
                                {formatLocation(organization.location)}
                            </Table.Cell>
                            <Table.Cell>
                                <Button
                                    size="small"
                                    primary
                                    onClick={() =>
                                        setEditingOrgId(organization.id)
                                    }
                                >
                                    Edit
                                </Button>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
            <Pagination
                defaultActivePage={currentPage}
                totalPages={Math.ceil(organizations.length / PER_PAGE)}
                onPageChange={setCurrentPage}
            />
            <EditOrganizationModal
                organization={editingOrganization}
                open={Boolean(editingOrganization)}
                onClose={() => setEditingOrgId(null)}
                onSave={onSave}
                saving={updating}
                organizationTypeOptions={organizationTypeOptions}
            />
        </Fragment>
    )
}

export default OrgReview
